import * as React from "react";
import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { formatCurrency } from "kanopi-components/Currency";
import { Notification } from "baseui/notification";
import { TPaymentFormProps } from "./types";
import { usePayment } from "./usePayment";
import { useElements, useStripe, PaymentElement } from "@stripe/react-stripe-js";
import { ParagraphXSmall, ParagraphSmall, ParagraphMedium } from "baseui/typography";
import { HeadingLarge, HeadingMedium } from "kanopi-components/typography";
import { StyledDivider, SIZE } from "baseui/divider";

const StripeForm: React.FC<TPaymentFormProps> = ({ riskProfile = {}, onSubmit }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [stripeLoading, setStripeLoading] = React.useState(false);
  const [stripeError, setStripeError] = React.useState<StripeError>(null);
  const stripeReady = !!stripe;
  const [collectedPaymentMethod, setCollectedPaymentMethod] = React.useState<
    object | null
  >(null);
  const {
    submit: paymentSubmit,
    loading: paymentLoading,
    getPaymentBreakdown,
    paymentBreakdown,
  } = usePayment({
    riskProfileId: riskProfile.id,
    onSubmit,
  });
  const disableSubmit = !stripeReady || stripeLoading || paymentLoading;

  const handleError = (error) => {
    setStripeLoading(false);
    setStripeError(error.message);
  };
  const PaymentBreakdownSection = (paymentBreakdown) => {
    return (
      <Block display="flex" flexDirection="column" alignItems="center">
        <HeadingMedium marginTop="scale1400" center>
          Payment summary
        </HeadingMedium>
        {paymentBreakdown.surcharge && (
          <ParagraphMedium align="center">
            A credit card surcharge of ${formatCurrency(paymentBreakdown.surcharge)} has
            been applied to your premium. This surcharge reflects additional processing
            fees incurred for credit card payments.
          </ParagraphMedium>
        )}
        <ParagraphMedium align="center">
          Please review the payment breakdown below to see the final amount due:
        </ParagraphMedium>
        <ParagraphSmall marginBottom={0}>
          <strong>Premium:</strong> ${formatCurrency(paymentBreakdown.premium)}
        </ParagraphSmall>
        {paymentBreakdown.surcharge && (
          <ParagraphXSmall marginTop={0}>
            <strong>Surcharge:</strong> ${formatCurrency(paymentBreakdown.surcharge)}
          </ParagraphXSmall>
        )}
        <Block width="100%" margin="auto">
          <StyledDivider $size={SIZE.cell} />
        </Block>
        <ParagraphMedium>
          <strong>Total amount:</strong> ${formatCurrency(paymentBreakdown.total)}
        </ParagraphMedium>
        <Block display="flex" justifyContent="center" marginTop="scale1200">
          <Button
            type="submit"
            kind="primary"
            disabled={disableSubmit}
            isLoading={stripeLoading || paymentLoading}
            wide
            onClick={stripeSubmit}
            overrides={{
              BaseButton: {
                style: ({ $theme }) => ({
                  [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
                    width: "100%",
                  },
                }),
              },
            }}
          >
            Pay
          </Button>
        </Block>
      </Block>
    );
  };

  const stripePaymentBreakdown = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    try {
      setStripeLoading(true);

      const { error: submitError } = await elements.submit();
      if (submitError) {
        handleError(submitError);
        return;
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            address: {
              postal_code: "3000",
              country: "AU",
            },
          },
        },
      });
      setStripeError(error);
      if (!error) {
        setCollectedPaymentMethod(paymentMethod);
        getPaymentBreakdown(paymentMethod.id);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setStripeLoading(false);
    }
  };

  const stripeSubmit = async () => {
    if (collectedPaymentMethod) {
      paymentSubmit({
        payment_method_id: collectedPaymentMethod.id,
      });
    }
  };

  return (
    <>
      {paymentBreakdown ? (
        PaymentBreakdownSection(paymentBreakdown)
      ) : (
        <form onSubmit={stripePaymentBreakdown}>
          {!riskProfile?.quotes?.[0].product?.skipPayments && (
            <HeadingLarge marginTop="scale1400" center>
              Pay via credit card
            </HeadingLarge>
          )}
          <PaymentElement />
          {stripeError && (
            <Notification
              kind="negative"
              overrides={{ Body: { style: { width: "auto" } } }}
            >
              {stripeError.type === "card_error"
                ? stripeError.message
                : "There was an error sending your payment request, please try again."}
            </Notification>
          )}
          <Block display="flex" justifyContent="center" marginTop="scale1200">
            <Button
              type="submit"
              kind="primary"
              disabled={disableSubmit}
              isLoading={stripeLoading || paymentLoading}
              wide
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
                      width: "100%",
                    },
                  }),
                },
              }}
            >
              Next
            </Button>
          </Block>
        </form>
      )}
    </>
  );
};

export { StripeForm };
